// src/nativeSocialAuth.js
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import {
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { firebaseAuth } from "./firebase";

let initPromise = null;

function withTimeout(promise, ms, label = "operation") {
  let t;
  const timeout = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(t));
}

function logFirebaseError(prefix, e) {
  console.log(prefix);
  console.log("name:", e?.name);
  console.log("code:", e?.code);
  console.log("message:", e?.message);
  console.log("stack:", e?.stack);
  console.log("raw error object:", e);
}

export async function initNativeSocialLogin() {
  if (!Capacitor.isNativePlatform()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;
    const iosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;

    console.log("[SocialLogin] init webClientId =", webClientId);
    console.log("[SocialLogin] init iosClientId =", iosClientId);

    if (!webClientId) throw new Error("Missing VITE_GOOGLE_WEB_CLIENT_ID");
    if (Capacitor.getPlatform() === "ios" && !iosClientId) {
      throw new Error("Missing VITE_GOOGLE_IOS_CLIENT_ID (required on iOS)");
    }

    await SocialLogin.initialize({
      google: {
        webClientId,
        iOSClientId: Capacitor.getPlatform() === "ios" ? iosClientId : undefined,
        // iOSServerClientId: webClientId, // keep removed for now
      },
    });

    console.log("[SocialLogin] initialized ✅");
  })();

  return initPromise;
}

export async function nativeGoogleToFirebase() {
  await initNativeSocialLogin();

  console.log("[Google native] starting SocialLogin.login...");
  const res = await withTimeout(
    SocialLogin.login({ 
      provider: "google", 
      options: { 
        forceCodeForRefreshToken: true  // forces account picker on Android
      } 
    }),
    20000,
    "SocialLogin.login(google)"
  );

  const idToken =
    res?.result?.idToken ||
    res?.result?.authentication?.idToken ||
    res?.result?.id_token ||
    res?.idToken ||
    res?.id_token ||
    null;

  const accessToken =
    res?.result?.accessToken?.token ||
    res?.result?.accessToken ||
    res?.accessToken ||
    res?.result?.authentication?.accessToken ||
    null;

  console.log("[Google native] got idToken?", !!idToken, "accessToken?", !!accessToken);

  if (!idToken && !accessToken) {
    console.log("[Google native] raw response:", res);
    throw new Error("Google native login returned neither idToken nor accessToken");
  }

  const credential = idToken
    ? GoogleAuthProvider.credential(idToken)
    : GoogleAuthProvider.credential(null, accessToken);

  console.log("[Google native] signing into Firebase with credential...");

  let userCred;
  try {
    userCred = await withTimeout(
      signInWithCredential(firebaseAuth, credential),
      20000,
      "Firebase signInWithCredential(google)"
    );
  } catch (e) {
    logFirebaseError("🔥 Firebase signInWithCredential(google) FAILED", e);
    throw e;
  }

  console.log("[Google native] Firebase sign-in OK:", userCred?.user?.uid);
  return userCred;
}

export async function nativeAppleToFirebase() {
  await initNativeSocialLogin();

  console.log("[Apple native] starting SocialLogin.login...");
  const res = await withTimeout(
    SocialLogin.login({ provider: "apple", options: { scopes: ["email", "name"] } }),
    20000,
    "SocialLogin.login(apple)"
  );

  const idToken =
    res?.result?.identityToken ||
    res?.result?.idToken ||
    res?.result?.identity_token ||
    res?.idToken ||
    res?.identityToken ||
    null;

  console.log("[Apple native] got idToken?", !!idToken);

  if (!idToken) {
    console.log("[Apple native] raw response:", res);
    throw new Error("Apple native login returned no idToken");
  }

  const provider = new OAuthProvider("apple.com");
  const credential = provider.credential({ idToken });

  console.log("[Apple native] signing into Firebase with credential...");

  let userCred;
  try {
    userCred = await withTimeout(
      signInWithCredential(firebaseAuth, credential),
      20000,
      "Firebase signInWithCredential(apple)"
    );
  } catch (e) {
    logFirebaseError("🔥 Firebase signInWithCredential(apple) FAILED", e);
    throw e;
  }

  console.log("[Apple native] Firebase sign-in OK:", userCred?.user?.uid);
  return userCred;
}
